import React from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
  Paper,
  ListItemButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard,
  People,
  Event,
  Message,
  Receipt,
  Settings,
  ExitToApp,
  Notifications,
  Menu as MenuIcon,
  VideoCall,
  AttachMoney,
  Circle,
  CalendarMonth,
  ChevronLeft,
  ChevronRight,
  Assignment,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../hooks/useNotifications';
import { counselingRequestsService } from '../../services/counselingRequestsService';
import { useIdleTimer } from '../../hooks/useIdleTimer';
import IdleTimeoutDialog from '../IdleTimeoutDialog';

const drawerWidth = 240;
const miniDrawerWidth = 64;

export const Layout: React.FC = () => {
  const { state, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const currentDrawerWidth = sidebarCollapsed ? miniDrawerWidth : drawerWidth;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notificationAnchor, setNotificationAnchor] = React.useState<null | HTMLElement>(null);
  const { notifications, unreadCount, unreadMessageCount, loading, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();

  // Badge count of new (unreviewed) Counseling Request Form submissions —
  // admin/staff only, polled the same way unread message counts are.
  const [newCounselingRequestsCount, setNewCounselingRequestsCount] = React.useState(0);
  React.useEffect(() => {
    const userRole = state.user?.role as string;
    if (userRole !== 'admin' && userRole !== 'staff') {
      return;
    }

    let cancelled = false;
    const fetchNewRequestsCount = async () => {
      try {
        const page = await counselingRequestsService.list({ status: 'new', limit: 1 });
        if (!cancelled) setNewCounselingRequestsCount(page.count || 0);
      } catch (err) {
        console.error('Failed to fetch new counseling requests count:', err);
      }
    };

    fetchNewRequestsCount();
    const interval = setInterval(fetchNewRequestsCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [state.user?.role]);

  // Suppress idle auto-logout while the user is in an active telehealth video session
  const isInTelehealthSession = /^\/telehealth\/(session|join)\//.test(location.pathname);

  // Auto sign-out after 5 minutes of inactivity → 3-minute countdown
  const { showWarning, secondsLeft, resetTimer } = useIdleTimer(isInTelehealthSession ? () => {} : logout);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    const userRole = state.user?.role as string;
    if (userRole === 'patient' || userRole === 'client') {
      navigate('/client/settings');
    } else if (userRole === 'therapist') {
      navigate('/therapist/profile');
    } else if (userRole === 'staff') {
      navigate('/staff/profile');
    } else if (userRole === 'admin') {
      navigate('/admin/settings');
    }
  };

  const handleSettingsClick = () => {
    handleMenuClose();
    const userRole = state.user?.role as string;
    if (userRole === 'patient' || userRole === 'client') {
      navigate('/client/settings');
    } else if (userRole === 'therapist') {
      navigate('/therapist/settings');
    } else if (userRole === 'staff') {
      navigate('/staff/settings');
    } else if (userRole === 'admin') {
      navigate('/admin/settings');
    }
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
    // Always re-fetch so new notifications appear immediately
    if (!loading) {
      void fetchNotifications();
    }
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationItemClick = async (notificationId: string, relatedObjectId?: string) => {
    await markAsRead(notificationId);
    handleNotificationClose();
    
    // Navigate to messages if it's a message notification
    if (relatedObjectId) {
      navigate('/messages');
    }
  };

  // Navigation items based on user role
  const getNavigationItems = () => {
    const items = [];

    // Check user role and show appropriate navigation
    // Note: Backend sends string roles, not enum values
    const userRole = state.user?.role as string;

    if (userRole === 'patient' || userRole === 'client') {
      // Client navigation - core features they need
      items.push(
        { text: 'My Dashboard', icon: <Dashboard />, path: '/client' },
        { text: 'My Appointments', icon: <Event />, path: '/appointments' },
        { text: 'Messages', icon: <Badge badgeContent={unreadMessageCount} color="error"><Message /></Badge>, path: '/messages' },
        { text: 'Telehealth', icon: <VideoCall />, path: '/telehealth/dashboard' },
        { text: 'My Documents', icon: <Receipt />, path: '/client/documents' },
        { text: 'Billing', icon: <AttachMoney />, path: '/client/billing' },
      );
    } else if (userRole === 'therapist') {
      // Therapist navigation
      items.push(
        { text: 'Dashboard', icon: <Dashboard />, path: '/therapist' },
        { text: 'My Clients', icon: <People />, path: '/therapist/patients' },
        { text: 'Client Documents', icon: <Receipt />, path: '/therapist/client-consents' },
        { text: 'Appointments', icon: <Event />, path: '/appointments' },
        { text: 'Schedule', icon: <CalendarMonth />, path: '/schedule' },
        { text: 'SOAP Notes', icon: <Settings />, path: '/therapist/soap-notes' },
        { text: 'Messages', icon: <Badge badgeContent={unreadMessageCount} color="error"><Message /></Badge>, path: '/messages' },
        { text: 'Telehealth', icon: <VideoCall />, path: '/telehealth/dashboard' },
      );
    } else {
      // Admin/default navigation (shows all features)
      items.push(
        { text: 'Admin Dashboard', icon: <Dashboard />, path: '/admin' },
        { text: 'Client Management', icon: <People />, path: '/admin/patients' },
        { text: 'Counseling Requests', icon: <Badge badgeContent={newCounselingRequestsCount} color="error"><Assignment /></Badge>, path: '/admin/counseling-requests' },
        { text: 'Client Documents', icon: <Receipt />, path: '/admin/client-consents' },
        { text: 'Appointments', icon: <Event />, path: '/appointments' },
        { text: 'Schedule', icon: <CalendarMonth />, path: '/schedule' },
        { text: 'SOAP Notes', icon: <Receipt />, path: '/admin/soap-notes' },
        { text: 'Messages', icon: <Badge badgeContent={unreadMessageCount} color="error"><Message /></Badge>, path: '/messages' },
        { text: 'Telehealth', icon: <VideoCall />, path: '/telehealth/dashboard' },
        { text: 'Billing', icon: <AttachMoney />, path: '/admin/billing' },
        { text: 'Reports', icon: <Settings />, path: '/admin/reports' },
      );
    }

    // Original role-based logic (commented out during bypass):
    /*
    if (hasRole(UserRole.ADMIN)) {
      items.push(
        { text: 'Admin Dashboard', icon: <Dashboard />, path: '/admin' },
        { text: 'User Management', icon: <People />, path: '/admin/users' },
        { text: 'Billing Overview', icon: <Receipt />, path: '/admin/billing' },
        { text: 'System Reports', icon: <Settings />, path: '/admin/reports' },
      );
    }

    if (hasRole([UserRole.THERAPIST, UserRole.STAFF])) {
      items.push(
        { text: 'Dashboard', icon: <Dashboard />, path: '/therapist' },
        { text: 'Clients', icon: <People />, path: '/therapist/patients' },
        { text: 'Appointments', icon: <Event />, path: '/therapist/appointments' },
        { text: 'SOAP Notes', icon: <Receipt />, path: '/therapist/soap-notes' },
        { text: 'Messages', icon: <Message />, path: '/therapist/messages' },
      );
    }

    if (hasRole(UserRole.CLIENT)) {
      items.push(
        { text: 'My Dashboard', icon: <Dashboard />, path: '/client' },
        { text: 'My Appointments', icon: <Event />, path: '/client/appointments' },
        { text: 'Messages', icon: <Message />, path: '/client/messages' },
        { text: 'Documents', icon: <Receipt />, path: '/client/documents' },
      );
    }
    */

    return items;
  };

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header with title and collapse toggle */}
      <Toolbar sx={{ justifyContent: sidebarCollapsed ? 'center' : 'space-between', px: sidebarCollapsed ? 0 : 2, minHeight: 64 }}>
        {!sidebarCollapsed && (
          <Typography variant="h6" noWrap component="div" color="primary" sx={{ fontSize: '1rem' }}>
            SafeHaven EHR
          </Typography>
        )}
        <Tooltip title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton
            onClick={() => setSidebarCollapsed(prev => !prev)}
            size="small"
            sx={{ color: 'primary.main' }}
          >
            {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </Tooltip>
      </Toolbar>
      <Divider />
      <List sx={{ pt: 1 }}>
        {getNavigationItems().map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
            <Tooltip title={sidebarCollapsed ? item.text : ''} placement="right">
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  justifyContent: sidebarCollapsed ? 'center' : 'initial',
                  px: sidebarCollapsed ? 0 : 2,
                  minHeight: 48,
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: sidebarCollapsed ? 0 : 2, justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={item.text} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          ml: { sm: `${currentDrawerWidth}px` },
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {isMobile ? 'SafeHaven' : 'SafeHaven EHR System'}
          </Typography>

          {/* Notifications */}
          <IconButton color="inherit" sx={{ mr: { xs: 1, sm: 2 } }} onClick={handleNotificationClick}>
            <Badge badgeContent={unreadCount} color="error">
              <Notifications />
            </Badge>
          </IconButton>

          {/* Notification Popover */}
          <Popover
            open={Boolean(notificationAnchor)}
            anchorEl={notificationAnchor}
            onClose={handleNotificationClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <Paper sx={{ width: { xs: 320, sm: 360 }, maxHeight: 400 }}>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Notifications</Typography>
                {unreadCount > 0 && (
                  <Button size="small" onClick={() => void markAllAsRead()}>
                    Mark all read
                  </Button>
                )}
              </Box>
              <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                {loading ? (
                  <ListItem>
                    <ListItemText primary="Loading..." />
                  </ListItem>
                ) : notifications.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No notifications" secondary="You're all caught up!" />
                  </ListItem>
                ) : (
                  notifications.map((notification) => (
                    <ListItemButton
                      key={notification.id}
                      onClick={() => void handleNotificationItemClick(notification.id, notification.related_object_id)}
                      sx={{
                        backgroundColor: notification.isRead ? 'transparent' : 'action.hover',
                        '&:hover': {
                          backgroundColor: 'action.selected',
                        },
                      }}
                    >
                      <ListItemIcon>
                        {!notification.isRead && (
                          <Circle sx={{ fontSize: 10, color: 'primary.main' }} />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.title}
                        secondary={notification.body}
                        primaryTypographyProps={{ fontWeight: notification.isRead ? 'normal' : 'bold' }}
                      />
                    </ListItemButton>
                  ))
                )}
              </List>
            </Paper>
          </Popover>

          {/* User Menu */}
          <Button
            onClick={handleProfileMenuOpen}
            color="inherit"
            startIcon={
              <Avatar sx={{ width: 32, height: 32 }}>
                {state.user?.firstName?.charAt(0)}
              </Avatar>
            }
            sx={{ minWidth: isMobile ? 0 : 'auto', px: isMobile ? 1 : 2 }}
          >
            {!isMobile ? `${state.user?.firstName} ${state.user?.lastName}` : ''}
          </Button>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleProfileClick}>Profile</MenuItem>
            <MenuItem onClick={handleSettingsClick}>Settings</MenuItem>
            <Divider />
            <MenuItem onClick={() => handleLogout()}>
              <ExitToApp sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: Math.min(drawerWidth, 280) },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: currentDrawerWidth,
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Idle-timeout warning dialog — suppressed during active telehealth sessions */}
      <IdleTimeoutDialog
        open={showWarning && !isInTelehealthSession}
        secondsLeft={secondsLeft}
        onStayLoggedIn={resetTimer}
        onLogoutNow={logout}
      />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 3 },
          width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
          height: '100vh',
          overflowY: 'auto',
          boxSizing: 'border-box',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
